import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}
const ease = [0.23, 1, 0.32, 1] as const;

/**
 * Lille formular-vindue: glider op nedefra på mobil, står i midten på computer.
 * Lukkes med X, Esc eller klik udenfor.
 */
export function Sheet({
  open,
  title,
  onClose,
  children
}: SheetProps) {
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    // Fokus ind i vinduet, så tastatur og skærmlæser lander det rigtige sted.
    const timer = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('input:not([type="date"]), select, textarea, button[type="submit"]')?.focus({
        preventScroll: true
      });
    }, 30);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(timer);
    };
  }, [open]);
  return <AnimatePresence data-id="element-539" data-mp="imuj9lw0ko15">
      {open && <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label={title} data-id="element-540" data-mp="ejes352prfvu">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18
      }} className="absolute inset-0 bg-black/60" onClick={onClose} data-id="element-541" data-mp="shuysw0kt6dz" />
          <motion.div ref={panelRef} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 28
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 28
      }} transition={{
        duration: 0.24,
        ease
      }} className="relative max-h-[92vh] w-full overflow-y-auto rounded-t-card border border-line bg-white px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 sm:max-w-md sm:rounded-card sm:p-6" data-id="element-542" data-mp="xbh1pwgxtvwl">
            <div className="flex items-center justify-between gap-3" data-id="element-543" data-mp="cxbfkndcl3ct">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-544" data-mp="houj4d6o2ne1">{title}</h2>
              <button type="button" onClick={onClose} aria-label="Luk" className="-mr-2 inline-flex h-10 w-10 items-center justify-center rounded-xl text-ink-muted transition-colors duration-150 hover:bg-canvas-deep hover:text-ink" data-id="element-545" data-mp="moftdjraei62">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-546" data-mp="10ogsatnzrfiw" />
              </button>
            </div>
            <div className="mt-4" data-id="element-547" data-mp="2ye8gfmab0ozh">{children}</div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}